import {useQuery} from '@github-ui/react-query'
import {useCallback} from 'react'

import {SystemColumnId} from '../../api/columns/contracts/memex-column'
import {apiGetSuggestedMilestones} from '../../api/memex-items/api-get-suggested-milestones'
import type {GetSuggestedMilestonesResponse, SuggestedMilestone} from '../../api/memex-items/contracts'
import {ItemType} from '../../api/memex-items/item-type'
import {apiSidePanelGetSuggestedMilestonesForItem} from '../../api/side-panel/api-side-panel-get-suggestions'
import {ItemKeyType} from '../../api/side-panel/contracts'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useSetColumnValue} from '../column-values/use-set-column-value'
import {useFindMemexItem} from '../memex-items/use-find-memex-item'
import {makeApiCall} from './suggestions-state-provider'
import {useSuggestionsStableContext} from './use-suggestions-stable-context'

type FetchSuggestedMilestonesHookType = {
  /**
   * Fetches the suggested milestones from the server for a given item.
   * Updates the item's milestone column with the first suggested milestone that is
   * marked as selected.
   * @returns A promise that resolves to an array of suggested milestones.
   * @param memexItemId The id of the item to fetch the suggested milestones for.
   */
  fetchSuggestedMilestones: (model: MemexItemModel) => Promise<Array<SuggestedMilestone> | Error | undefined>
}

type UseSuggestedMilestones = {
  /** The list of milestones that are suggested for the current item. */
  suggestedMilestones: Array<SuggestedMilestone>
}

/**
 * Fetches and returns the list of suggested milestones for a given item model. If the model changes, the query data
 * will automatically re-fetch and return new data. Data is cached on a per-repository basis if the item belongs to a repository,
 * otherwise it is cached on a per-item basis.
 *
 * If the `memex_milestone_caching` feature flag is disabled, an empty array will be returned.
 */
export const useSuggestedMilestones = (model: MemexItemModel): UseSuggestedMilestones => {
  const {memex_milestone_caching} = useEnabledFeatures()
  const memexItemId = model?.memexItemId()
  const repositoryId = model?.ownerId()

  const {data} = useQuery<GetSuggestedMilestonesResponse>({
    // Milestones should be cached on a per-repository basis, rather than a per-item basis.
    queryKey: ['suggested-milestones', repositoryId ?? memexItemId],
    queryFn: () => {
      // Draft issues don't have repositoryIds
      if (repositoryId && memexItemId) {
        return apiGetSuggestedMilestones({memexProjectItemId: memexItemId})
      } else {
        return Promise.resolve({suggestions: []} satisfies GetSuggestedMilestonesResponse)
      }
    },
    staleTime: 1000 * 60, // Do not re-fetch data within 1 minute of last fetch
    enabled: memex_milestone_caching,
  })

  return {suggestedMilestones: data?.suggestions ?? []}
}

export const useFetchSuggestedMilestones = (): FetchSuggestedMilestonesHookType => {
  const {findMemexItem} = useFindMemexItem()
  const {setColumnValue} = useSetColumnValue()
  const {setSuggestedMilestonesForItem} = useSuggestionsStableContext()

  const fetchSuggestedMilestones = useCallback(
    async (model: MemexItemModel) => {
      setSuggestedMilestonesForItem(model.getSuggestionsCacheKey(), undefined)

      const {response, error} = await makeApiCall(() => {
        if (model.memexItemId?.()) {
          return apiGetSuggestedMilestones({memexProjectItemId: model.memexItemId()})
        } else {
          switch (model.contentType) {
            case ItemType.DraftIssue:
              return apiSidePanelGetSuggestedMilestonesForItem({
                kind: ItemKeyType.PROJECT_DRAFT_ISSUE,
                projectItemId: model.id,
              })
            case ItemType.Issue:
              return apiSidePanelGetSuggestedMilestonesForItem({
                kind: ItemKeyType.ISSUE,
                itemId: model.itemId(),
                repositoryId: model.ownerId(),
              })
            default:
              throw new Error(`Unsupported item type: ${model.contentType}`)
          }
        }
      })

      const memexItem = findMemexItem(model.id)

      if (memexItem && !error) {
        setColumnValue(memexItem, {
          memexProjectColumnId: SystemColumnId.Milestone,
          value: response?.suggestions.find(suggestedMilestone => suggestedMilestone.selected),
        })
      }

      const result = response?.suggestions ?? error

      setSuggestedMilestonesForItem(model.getSuggestionsCacheKey(), result)

      return result
    },
    [setSuggestedMilestonesForItem, findMemexItem, setColumnValue],
  )

  return {fetchSuggestedMilestones}
}
